/**
 * Registry of slide template metadata used by the editor: human-readable
 * labels, short descriptions, and `blank()` factories that seed a new slide.
 *
 * v1: Every picker entry produces a `BlockSlide` (free-form canvas) seeded
 * from the same copy/imagery as the legacy typed slides, for parity.
 */

import type { Block, Scrim } from './blocks';
import { createId } from './storage';
import type { BlockSlide, BlockSlidePreset, Slide, SlideBg } from './types';

export type TemplateCategory = 'Standard' | 'Recent decks';

export interface TemplateMeta {
  id: BlockSlidePreset;
  label: string;
  description: string;
  /** Picker section. Defaults to 'Standard'. */
  category?: TemplateCategory;
  /** Optional source-deck attribution (e.g. "CNE", "Showpass"). */
  source?: string;
  blank: () => Slide;
}

type TextBlockKind = import('./blocks').TextBlock['kind'];

function tb(
  kind: TextBlockKind,
  anchor: import('./blocks').Anchor,
  x: number,
  y: number,
  text: string,
  extra?: Partial<import('./blocks').TextBlock>,
): import('./blocks').TextBlock {
  return {
    id: createId('blk'),
    kind,
    anchor,
    x,
    y,
    text,
    ...extra,
  };
}

function scrim(direction: Scrim['direction'], stops: Scrim['stops'], color?: string): Scrim {
  return { direction, stops, color };
}

function blockSlide(
  preset: BlockSlidePreset,
  partial: Omit<BlockSlide, 'id' | 'type'> & { id?: string },
): BlockSlide {
  return {
    id: partial.id ?? createId('slide'),
    type: 'blocks',
    preset,
    bg: partial.bg,
    scrim: partial.scrim,
    textColor: partial.textColor,
    snapEnabled: partial.snapEnabled ?? true,
    blocks: partial.blocks,
  };
}

/** Build blocks + bg for each preset (legacy Telus Spark parity). */
export function blocksForPreset(preset: BlockSlidePreset): {
  bg?: SlideBg;
  scrim?: Scrim;
  textColor: BlockSlide['textColor'];
  blocks: Block[];
} {
  switch (preset) {
    case 'blank-blocks':
      return {
        textColor: 'light',
        bg: { color: '#0C1A3A' },
        blocks: [
          tb('eyebrow', 'top-left', 8, 10, 'Slide'),
          tb('headline', 'center-left', 8, 50, 'Your headline here.', { w: 55 }),
        ],
      };
    case 'cover':
      return {
        textColor: 'light',
        bg: { src: '/img/investor-bg-cover.jpg', position: 'center' },
        scrim: scrim('bottom-right', [
          { at: 0, opacity: 0 },
          { at: 55, opacity: 0.2 },
          { at: 100, opacity: 0.45 },
        ]),
        blocks: [
          {
            id: createId('blk'),
            kind: 'logo',
            anchor: 'top-left',
            x: 10,
            y: 10,
            src: '/img/robin-logo-alt.png',
            opacity: 0.9,
            w: 28,
          },
          tb('eyebrow', 'top-left', 10, 14, 'For the team'),
          tb('headline', 'center-left', 10, 48, 'A one-line hook that lands the value.', { w: 52 }),
          tb('subheadline', 'center-left', 10, 62, 'One or two sentences to set up what follows.', {
            w: 48,
          }),
          tb('caption', 'bottom-left', 10, 82, 'Prepared by'),
          tb('body', 'bottom-left', 10, 86, 'Your name · Robin'),
          tb('caption', 'bottom-right', 90, 88, 'Two weeks from handshake to live', {
            anchor: 'bottom-right',
            x: 90,
            y: 88,
          }),
        ],
      };
    case 'right-column':
      return {
        textColor: 'light',
        bg: { src: '/img/investor-bg-gap.png', position: 'left', color: '#C4704A' },
        scrim: scrim('left', [
          { at: 0, opacity: 0.55 },
          { at: 45, opacity: 0.25 },
          { at: 100, opacity: 0 },
        ]),
        blocks: [
          tb('eyebrow', 'center-right', 88, 42, 'What it is', {
            anchor: 'center-right',
            x: 88,
            y: 42,
          }),
          tb('headline', 'center-right', 88, 50, 'The one-sentence definition.', {
            anchor: 'center-right',
            x: 88,
            y: 50,
            w: 38,
            textAlign: 'right',
          }),
          tb(
            'lede',
            'center-right',
            88,
            62,
            'A paragraph that fills in the what and the how without getting cute.',
            {
              anchor: 'center-right',
              x: 88,
              y: 62,
              w: 36,
              textAlign: 'right',
            },
          ),
          tb('caption', 'center-right', 88, 82, 'Running now: Brand · Brand · Brand', {
            anchor: 'center-right',
            x: 88,
            y: 82,
            textAlign: 'right',
          }),
        ],
      };
    case 'transcript':
      return {
        textColor: 'dark',
        bg: { src: '/img/photoslop/email-banner-robin-20260416-135134.png', color: '#FCF7F1' },
        blocks: [
          tb('eyebrow', 'center-left', 12, 48, 'What it sounds like on the floor'),
          tb('headline', 'center-left', 12, 54, 'Real questions. Real answers.'),
          tb('lede', 'center-left', 12, 66, 'Three visitors, same phone number.'),
          {
            id: createId('blk'),
            kind: 'conversation',
            anchor: 'center-right',
            x: 78,
            y: 50,
            w: 32,
            label: 'Live thread',
            thread: [
              { from: 'user', text: 'where do we park?' },
              {
                from: 'robin',
                text: 'Main lot off Saint Georges Drive. Free parking for members.',
              },
              { from: 'user', text: 'when is the next show?' },
              { from: 'robin', text: 'Next show: 2:15pm. Doors open 2:05.' },
            ],
            footer: 'Visitor · Robin · live exchange',
          },
        ],
      };
    case 'cards':
      return {
        textColor: 'light',
        bg: {
          src: '/img/photoslop/robin-spark-cute-16x9.png',
          position: 'right',
          scale: 1.15,
          color: '#1C3D3D',
        },
        scrim: scrim('right', [
          { at: 0, opacity: 0.5 },
          { at: 60, opacity: 0.15 },
          { at: 100, opacity: 0 },
        ]),
        blocks: [
          tb('eyebrow', 'center-left', 10, 40, 'Not just Q&A'),
          tb('headline', 'center-left', 10, 48, 'A direct line to every guest.', { w: 45 }),
          tb('lede', 'center-left', 10, 60, 'One paragraph explaining why this matters.', {
            w: 42,
          }),
          {
            id: createId('blk'),
            kind: 'bullets',
            anchor: 'bottom-left',
            x: 10,
            y: 78,
            w: 80,
            items: [
              'Card one — One sentence that sells the card.',
              'Card two — One sentence that sells the card.',
              'Card three — One sentence that sells the card.',
              'Card four — One sentence that sells the card.',
            ],
          },
        ],
      };
    case 'qr':
      return {
        textColor: 'light',
        bg: {
          src: '/img/photoslop/generated/gen-robin-wearing-a-telus-spark-logo-lanyard-1777058533711.png',
          position: 'left',
          color: '#1c6a4a',
        },
        scrim: scrim('left', [
          { at: 0, opacity: 0.65 },
          { at: 50, opacity: 0.2 },
          { at: 100, opacity: 0 },
        ]),
        blocks: [
          tb('eyebrow', 'center-left', 12, 44, 'Try it now'),
          tb('headline', 'center-left', 12, 52, 'Text Robin yourself.', { w: 40 }),
          tb('accent', 'center-left', 12, 62, '(825) 957-2281'),
          tb(
            'body',
            'center-left',
            12,
            70,
            'Text SPARK to opt in. Same flow your guests would use on-site.',
            { w: 38 },
          ),
          {
            id: createId('blk'),
            kind: 'qr',
            anchor: 'center-right',
            x: 82,
            y: 52,
            value: 'sms:+18259572281?body=SPARK',
            caption: 'Scan or text Robin',
            w: 22,
          },
          tb('caption', 'center-right', 82, 72, '(825) 957-2281 · text SPARK', {
            textAlign: 'center',
          }),
        ],
      };
    case 'problems':
      return {
        textColor: 'light',
        bg: {
          src: '/img/photoslop/robin-coral-tags-16x9.png',
          position: 'center',
          flipX: true,
          color: '#F4B9A8',
        },
        scrim: scrim('right', [
          { at: 0, opacity: 0.45 },
          { at: 100, opacity: 0.1 },
        ]),
        blocks: [
          tb('eyebrow', 'center-right', 88, 36, 'What this actually solves'),
          tb('headline', 'center-right', 88, 44, 'Two problems. One text line.', {
            w: 40,
            textAlign: 'right',
          }),
          {
            id: createId('blk'),
            kind: 'bullets',
            anchor: 'center-right',
            x: 88,
            y: 58,
            w: 38,
            items: [
              'Problem 1 — First angle. Second angle. Third angle.',
              'Problem 2 — First angle. Second angle. Third angle.',
            ],
          },
        ],
      };
    case 'pricing':
      return {
        textColor: 'dark',
        bg: { src: '/img/photoslop/trillick-market-bg.png', color: '#FCF7F1' },
        blocks: [
          tb('eyebrow', 'top-left', 10, 12, 'What it costs'),
          tb('headline', 'top-left', 10, 18, 'Start small. Scale if it works.', { w: 55 }),
          tb(
            'lede',
            'top-left',
            10,
            30,
            'One event to prove it. Month-to-month after, only if you want it.',
            { w: 50 },
          ),
          tb(
            'body',
            'center-left',
            10,
            48,
            'Pilot — $799 CAD · one event\nOne weekend. One number. One test.\n• Up to 1,500 opt-ins\n• 8,000 texts\n• Full build, launch, and training',
            {
              w: 42,
            },
          ),
          tb(
            'body',
            'center-right',
            55,
            48,
            'Always-On — Monthly if the pilot lands\nRoll Robin across every event.\n• Unlimited events\n• Sponsor shoutouts\n• Cancel any time',
            {
              w: 42,
            },
          ),
          tb(
            'accent',
            'bottom-left',
            10,
            90,
            'No lock-in. You see the numbers before you commit to anything bigger.',
          ),
        ],
      };
    case 'two-column':
      return {
        textColor: 'dark',
        bg: { src: '/img/photoslop/trillick-slide4-bg.png', position: 'bottom', color: '#FCF7F1' },
        blocks: [
          tb('eyebrow', 'top-left', 10, 12, 'Who does what'),
          tb('headline', 'top-left', 10, 18, 'We build it and train you. You run it.', { w: 70 }),
          tb(
            'lede',
            'top-left',
            10,
            32,
            'You own the voice. We own the product and the backstop.',
            { w: 55 },
          ),
          tb(
            'body',
            'center-left',
            12,
            52,
            'Us\n→ Build the knowledge base from your content.\n→ Launch the number and train your team.\n→ Stay on call for support and updates.',
            {
              w: 38,
            },
          ),
          tb(
            'body',
            'center-right',
            62,
            52,
            'You\n→ Own the content and voice.\n→ Send announcements on your schedule.\n→ Review what guests ask and refine.',
            {
              w: 38,
            },
          ),
        ],
      };
    case 'ask':
      return {
        textColor: 'light',
        bg: {
          src: '/img/photoslop/linkedin-duo-soft-20260408-155259.png',
          position: 'right',
          color: '#1c6a4a',
        },
        scrim: scrim('left', [
          { at: 0, opacity: 0.6 },
          { at: 50, opacity: 0.15 },
          { at: 100, opacity: 0 },
        ]),
        blocks: [
          tb('eyebrow', 'center-left', 12, 44, 'Next step'),
          tb('headline', 'center-left', 12, 52, 'Let us show you a demo.', { w: 45 }),
          tb(
            'lede',
            'center-left',
            12,
            64,
            'Thirty minutes. Live walkthrough. Take your questions. No commitment.',
            { w: 42 },
          ),
          tb('caption', 'center-left', 12, 80, 'robin.guide · hello@robin.guide'),
        ],
      };

    // ── Recent-deck templates ─────────────────────────────────────────
    // Modeled on the Canadian National Exhibition pitch deck cover slide.
    // Hero copy hard-anchored top-right with a directional scrim that
    // darkens the upper-right corner; byline (avatar + name) bottom-left.
    case 'cne-cover':
      return {
        textColor: 'light',
        bg: {
          src: '/api/photoslop/asset/generated/gen-2-wearing-an-event-2-lanyard-holing-a-ph-1777911051816.png',
          position: 'center',
          color: '#0c1a3a',
        },
        scrim: scrim('top-right', [
          { at: 0, opacity: 0 },
          { at: 60, opacity: 0.22 },
          { at: 100, opacity: 0.45 },
        ]),
        blocks: [
          {
            id: createId('blk'),
            kind: 'logo',
            anchor: 'top-left',
            x: 4,
            y: 5,
            src: '/img/robin-wordmark-alt.png',
            opacity: 0.55,
            w: 9,
          },
          tb('eyebrow', 'top-right', 95, 8, 'For Canadian National Exhibition leadership', {
            textAlign: 'right',
            w: 45,
          }),
          tb('headline', 'top-right', 95, 18, 'Reach every fairgoer by text.', {
            textAlign: 'right',
            w: 50,
          }),
          tb(
            'lede',
            'top-right',
            95,
            38,
            'Instant answers across the grounds. No app. No staff pulled. Always on.',
            {
              textAlign: 'right',
              w: 38,
            },
          ),
          tb('caption', 'bottom-left', 8, 90, 'Prepared by'),
          tb('body', 'bottom-left', 8, 94, 'Your name · Robin'),
        ],
      };

    // Modeled on CNE "Revenue rails" — bottom-anchored two-column pricing
    // story with big translucent numerals built into each rail's headline.
    // Numbers are baked into the eyebrow text ("01 · Tag") because we don't
    // have a custom rail block.
    case 'cne-rails':
      return {
        textColor: 'light',
        bg: {
          src: '/api/photoslop/asset/generated/gen-upscaled-version-of-2-1777912280483.png',
          position: 'center',
          color: '#0c1a3a',
        },
        scrim: scrim('bottom', [
          { at: 0, opacity: 0.55 },
          { at: 45, opacity: 0.65 },
          { at: 100, opacity: 0.85 },
        ]),
        blocks: [
          tb('eyebrow', 'top-left', 8, 14, 'The case'),
          tb('headline', 'top-left', 8, 20, 'The math works two ways.', { w: 55 }),
          tb('eyebrow', 'bottom-left', 8, 60, '01 · Your guests, your list'),
          tb('subheadline', 'bottom-left', 8, 65, 'Your guests. Fully yours.', { w: 40 }),
          tb(
            'body',
            'bottom-left',
            8,
            75,
            'Every guest who texts Robin becomes yours — phone, history, intent. Reach them August after August.',
            { w: 38 },
          ),
          tb(
            'accent',
            'bottom-left',
            8,
            92,
            'One season. A million opted-in numbers. Yours to keep.',
          ),
          tb('eyebrow', 'bottom-right', 92, 60, '02 · Sponsor inventory you can price', {
            textAlign: 'right',
          }),
          tb('subheadline', 'bottom-right', 92, 65, 'Sponsors, proven in chat.', {
            w: 40,
            textAlign: 'right',
          }),
          tb(
            'body',
            'bottom-right',
            92,
            75,
            'Sponsors get named in real conversations — sends, opens, replies, conversions tracked. Walk into renewals with proof.',
            {
              w: 38,
              textAlign: 'right',
            },
          ),
          tb('accent', 'bottom-right', 92, 92, 'One PDF. Every renewal. Every meeting.', {
            textAlign: 'right',
          }),
        ],
      };

    // Modeled on CNE / IAFE closer — right-anchored eyebrow + two-line
    // headline + italic accent + body + contact line, on top of a
    // left-darkened scrim so the hero photo (subject on the left) reads.
    case 'cne-closer':
      return {
        textColor: 'light',
        bg: {
          src: '/api/photoslop/asset/generated/gen-1-but-with-2-and-3-on-the-ride-as-well-n-1777916993760.png',
          position: 'left',
          color: '#1c6a4a',
        },
        scrim: scrim('left', [
          { at: 0, opacity: 0 },
          { at: 40, opacity: 0.45 },
          { at: 100, opacity: 0.78 },
        ]),
        blocks: [
          tb('eyebrow', 'center-right', 95, 38, "What's next", { textAlign: 'right' }),
          tb('headline', 'center-right', 95, 46, 'One pilot. One weekend.', {
            w: 48,
            textAlign: 'right',
          }),
          tb('subheadline', 'center-right', 95, 60, 'Real numbers.', {
            w: 35,
            textAlign: 'right',
          }),
          tb(
            'body',
            'center-right',
            95,
            70,
            'Pick a zone. Pick a weekend. We will build the Robin, train your staff, and ship a sponsor recap after.',
            {
              w: 40,
              textAlign: 'right',
            },
          ),
          tb('caption', 'bottom-right', 95, 92, 'robin.guide · hello@robin.guide', {
            textAlign: 'right',
          }),
        ],
      };

    // Modeled on Showpass-Partnerships "The setup" — center-left eyebrow,
    // a dual-headline pairing where the second line drops to ~55% opacity
    // for typographic contrast, a long lede, and a row of bullets along
    // the bottom-left.
    case 'showpass-setup':
      return {
        textColor: 'light',
        bg: { color: '#1AC799' },
        scrim: scrim('bottom', [
          { at: 0, opacity: 0.4 },
          { at: 100, opacity: 0.6 },
        ]),
        blocks: [
          tb('eyebrow', 'center-left', 5, 30, 'The setup'),
          tb('headline', 'center-left', 5, 38, 'An API is a feature.', { w: 55 }),
          tb('headline', 'center-left', 5, 50, 'A partnership is a business.', {
            w: 55,
            color: 'rgba(255,255,255,0.55)',
          }),
          tb(
            'lede',
            'center-left',
            5,
            68,
            'Platform deals die in two ways. Too shallow to move a number. Too heavy to ship before the next budget cycle. We built three shapes so there is always a live path forward.',
            {
              w: 58,
            },
          ),
          {
            id: createId('blk'),
            kind: 'bullets',
            anchor: 'bottom-left',
            x: 5,
            y: 92,
            w: 70,
            items: [
              'Start light.',
              'Go deep when it proves out.',
              'Exit anytime, without a rebuild.',
            ],
          },
        ],
      };

    // Modeled on Showpass-Partnerships "Three models" — top-right header
    // with eyebrow + huge headline + lede, then a center-right body block
    // describing the depth options. Designed for image subject on the left.
    case 'showpass-three-models':
      return {
        textColor: 'light',
        bg: {
          src: '/api/photoslop/asset/generated/gen-1-diving-into-a-underwater-pool-nose-fis-1777590050195.png',
          position: 'left',
          color: '#0c1a3a',
        },
        scrim: scrim('left', [
          { at: 0, opacity: 0 },
          { at: 40, opacity: 0.45 },
          { at: 100, opacity: 0.85 },
        ]),
        blocks: [
          tb('eyebrow', 'top-right', 95, 12, 'The three models', { textAlign: 'right' }),
          tb('headline', 'top-right', 95, 18, 'Pick your depth.', {
            w: 45,
            textAlign: 'right',
          }),
          tb(
            'body',
            'top-right',
            95,
            35,
            'From low-touch discovery to deep embedded product. There is a fit for every partner type.',
            {
              w: 35,
              textAlign: 'right',
            },
          ),
          tb('eyebrow', 'center-right', 95, 55, '01 · Light · Discovery', { textAlign: 'right' }),
          tb('subheadline', 'center-right', 95, 60, 'Showpass-listed Robin pages.', {
            w: 38,
            textAlign: 'right',
          }),
          tb('eyebrow', 'center-right', 95, 70, '02 · Embedded · Co-marketed', {
            textAlign: 'right',
          }),
          tb('subheadline', 'center-right', 95, 75, 'Robin lives inside the buy flow.', {
            w: 38,
            textAlign: 'right',
          }),
          tb('eyebrow', 'bottom-right', 95, 85, '03 · Deep · Joint product', {
            textAlign: 'right',
          }),
          tb('subheadline', 'bottom-right', 95, 90, 'Shared roadmap. Shared revenue.', {
            w: 38,
            textAlign: 'right',
          }),
        ],
      };

    default: {
      return {
        textColor: 'light' as const,
        bg: { src: '', color: '#0C1A3A' },
        blocks: [
          tb('eyebrow', 'top-left', 8, 10, 'Slide'),
          tb('headline', 'center-left', 8, 50, 'Your headline here.', { w: 55 }),
        ],
      };
    }
  }
}

export function buildBlockSlideFromPreset(preset: BlockSlidePreset): BlockSlide {
  const { bg, scrim: s, textColor, blocks } = blocksForPreset(preset);
  return blockSlide(preset, { bg, scrim: s, textColor, blocks });
}

/**
 * Distinct slide layouts only. Mirror variants (left ↔ right) are produced
 * by the editor's "Flip horizontally" action rather than separate presets,
 * so this registry stays small and the picker stays scannable.
 *
 * Removed mirror duplicates (still valid as `BlockSlidePreset` ids for
 * back-compat with stored decks):
 *   - cne-cover     → use `cover`   then Flip
 *   - cne-closer    → use `ask`     then Flip
 *   - problems      → use `cards`   (same headline + bullets shape)
 *   - pricing       → use `two-column` (same two-body shape)
 */
export const TEMPLATE_REGISTRY: TemplateMeta[] = [
  // ── Distinct layouts ──────────────────────────────────────────────────
  {
    id: 'cover',
    label: 'Cover',
    description: 'Hero headline + lede + byline. Use Flip to swap sides.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('cover'),
  },
  {
    id: 'right-column',
    label: 'Headline + lede',
    description: 'Single column of copy over a photo. Flippable.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('right-column'),
  },
  {
    id: 'transcript',
    label: 'Transcript',
    description: 'Headline + a live conversation thread.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('transcript'),
  },
  {
    id: 'cards',
    label: 'Headline + bullets',
    description: 'Headline / lede with a bulleted body. Flip to swap sides.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('cards'),
  },
  {
    id: 'qr',
    label: 'QR / try it now',
    description: 'Headline + lede + big QR code.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('qr'),
  },
  {
    id: 'two-column',
    label: 'Two-column comparison',
    description: 'Header on top, two body columns below — pricing, us/you, …',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('two-column'),
  },
  {
    id: 'ask',
    label: 'Closing CTA',
    description: 'Eyebrow, headline, lede, contact line. Flip for either side.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('ask'),
  },

  // ── Higher-density layouts ────────────────────────────────────────────
  {
    id: 'cne-rails',
    label: 'Numbered rails',
    description: 'Top header + two numbered rails along the bottom.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('cne-rails'),
  },
  {
    id: 'showpass-setup',
    label: 'Dual headline',
    description: 'Solid + faded contrast headline + supporting copy + bullets.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('showpass-setup'),
  },
  {
    id: 'showpass-three-models',
    label: 'Stacked options',
    description: 'Header + three labelled options (number · tag · title).',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('showpass-three-models'),
  },

  // ── Empty ─────────────────────────────────────────────────────────────
  {
    id: 'blank-blocks',
    label: 'Blank canvas',
    description: 'Minimal blocks — build your own layout.',
    category: 'Standard',
    blank: () => buildBlockSlideFromPreset('blank-blocks'),
  },
];

export function getTemplate(id: BlockSlidePreset): TemplateMeta | null {
  return TEMPLATE_REGISTRY.find((t) => t.id === id) ?? null;
}

export function makeBlankSlide(preset: BlockSlidePreset): Slide {
  const meta = getTemplate(preset);
  if (!meta) throw new Error(`Unknown slide preset: ${preset}`);
  return meta.blank();
}
